/**
 * Precise geolocation for the inbox-bound path (§8.1).
 *
 * A capture that could not be filed carries where you were when you wrote it, because "grab
 * milk" in a supermarket car park and "grab milk" at a desk are different thoughts — and
 * because it makes a terse note legible a week later. The watch already does this; this is
 * the browser's half of the same thing.
 *
 * **Everything here is optional, and optional means optional.** §8.1: a client that sends no
 * position is not degraded in any way. So this function resolves with `null` for every
 * failure mode there is — permission denied, no GPS, a desktop that has never known where it
 * is, a timeout, an insecure origin, a browser without the API at all — and never rejects.
 * A caller that forgets to handle an error cannot lose a capture over it, because there is no
 * error to forget.
 *
 * **A real GPS lock takes seconds, sometimes ten or more, and this is allowed to wait that
 * long.** That only works because the caller does not wait *with* it — `QuickAddCard` queues
 * the capture immediately (§0's durable outbox, `taskQueue.ts`) and lets this resolve in the
 * background, attaching whatever arrives before the queue actually sends. A form that stayed
 * disabled for `LOCATION_WAIT_MS` would make a second capture impossible until the first
 * one's fix showed up, which is the opposite of what capture is for.
 *
 * **It is never asked for on the path that becomes a task.** A task has nowhere to put a
 * position, so requesting one there would be a permission prompt and a GPS wake in exchange
 * for a value that gets dropped on arrival.
 *
 * **`primePosition` starts the fix early — the moment the reader starts typing an
 * inbox-bound title — so the wait overlaps with the time they spend writing instead of
 * starting fresh at submit.** `bestEffortPosition` picks up whatever `primePosition` already
 * has going rather than asking a second time. This matters for more than speed: a browser
 * whose permission prompt gets dismissed (not denied — a dismissal leaves the state at
 * `"prompt"`, still askable) would otherwise be asked again on every character typed if
 * priming itself weren't idempotent per capture — see the cache below.
 */

export type Position = { lat: number; lon: number };

/**
 * How long a capture is willing to wait for a precise fix before going without one.
 *
 * This no longer sits between pressing Add and the text being safe — the outbox already
 * makes those two things independent (see the module doc) — so it is sized for what a GPS
 * chip actually needs for a *precise* reading rather than for "instant": a cold lock outdoors
 * is commonly five to ten seconds, and this gives it room to finish rather than settling for
 * whatever a cell-tower estimate already had cached.
 */
export const LOCATION_WAIT_MS = 15_000;

/**
 * Whether asking would put a permission prompt in front of the owner right now.
 *
 * Used to skip the request entirely once it has been denied. Not used to skip the `"prompt"`
 * state: the prompt has to happen *somewhere* or the feature can never be turned on, and
 * doing it here is safe because nothing downstream waits on the answer — the timeout below
 * wins, the capture sends without a position, and the owner's choice applies from the next
 * one onward. A prompt you can ignore with no consequence is not a precondition.
 */
async function permissionState(): Promise<PermissionState | null> {
  if (!navigator.permissions?.query) return null;
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state;
  } catch {
    // Firefox has historically thrown on this descriptor. Not knowing is not a reason to
    // skip asking.
    return null;
  }
}

function requestPosition(): Promise<Position | null> {
  return new Promise<Position | null>((resolve) => {
    // Our own ceiling as well as the API's. `timeout` is honoured inconsistently, and the one
    // thing that must not happen is this hanging forever and never releasing the queued
    // record it's attached to.
    let settled = false;
    const done = (value: Position | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = setTimeout(() => done(null), LOCATION_WAIT_MS);

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        clearTimeout(timer);
        done({ lat: coords.latitude, lon: coords.longitude });
      },
      () => {
        // Denied, unavailable, or timed out. All the same answer: no position, no complaint.
        clearTimeout(timer);
        done(null);
      },
      {
        timeout: LOCATION_WAIT_MS,
        // A fresh reading, not a cached one — the entire point of waiting is a fix better
        // than whatever a short timeout would previously have settled for.
        maximumAge: 0,
        enableHighAccuracy: true,
      },
    );
  });
}

/** The one fix in flight (or already settled) for the capture currently being typed, if
 * priming has started one. `null` between captures — see `primePosition` and
 * `bestEffortPosition`. */
let primed: Promise<Position | null> | null = null;

/**
 * Start a fix early, before the reader has finished typing or decided where this is going.
 *
 * **A no-op if one is already running or already answered.** That single guard is what makes
 * it safe to call on every keystroke without a debounce of its own: the caller (`QuickAdd`)
 * still only calls this once, on the title field's empty-to-non-empty edge, but even if it
 * didn't, a second call here would find `primed` already set and do nothing — no repeated
 * permission prompt, no repeated GPS wake. It clears only when `bestEffortPosition` actually
 * consumes the result, which is what lets the *next* capture prime again.
 */
export function primePosition(): void {
  if (!navigator.geolocation || primed) return;
  primed = permissionState().then((state) => (state === "denied" ? null : requestPosition()));
}

/**
 * The owner's position, precisely, or `null` — which is an ordinary outcome, not a failure.
 *
 * Reuses whatever `primePosition` already started; asks fresh only if nothing was primed —
 * the destination was already chosen when typing began, say, so priming never ran (§8.1,
 * `QuickAddCard`).
 *
 * Rounded to six decimal places server-side (`services/inbox.py`), so there is no point
 * asking for anything the GPS chip itself cannot deliver, but no point asking for less either
 * now that nothing is waiting on this synchronously.
 */
export async function bestEffortPosition(): Promise<Position | null> {
  if (primed) {
    // Claimed *synchronously*, before the await below — not after it resolves. `add()` awaits
    // a few things (queueing, the mirror, the notice) before it ever gets here, and a reader
    // fast enough to start a second capture in that gap must see `primed` already cleared, or
    // their own `primePosition()` call would find someone else's fix still parked here and
    // treat it as its own — no-op instead of starting a fresh request, and this capture's
    // eventual `bestEffortPosition()` call would then hand it the *first* capture's location.
    const inFlight = primed;
    primed = null;
    return inFlight;
  }

  if (!navigator.geolocation) return null;
  if ((await permissionState()) === "denied") return null;
  return requestPosition();
}

/**
 * Test-only. Clears whatever `primePosition` has going, so one test's priming (a keystroke
 * that starts a fix and never submits, say) cannot leak into the next test in the same file —
 * `primed` is module state, and nothing resets it between tests on its own. Never called from
 * production code: a real capture that primed but was abandoned mid-type just sits there
 * until its own `LOCATION_WAIT_MS` timeout resolves it, exactly as intended, and forcing that
 * earlier would only make some *other* real caller re-request for no reason.
 */
export function resetPrimingForTests(): void {
  primed = null;
}
